"use client";

import React from "react";
import Container from "@/components/Layouts/Container/Container";
import ProductGallery from "@/components/ProductDetail/ProductGallery";
import ProductInfo from "@/components/ProductDetail/ProductInfo";
import AdditionalInfo from "@/components/ProductDetail/AdditionalInfo";
import { useFetchRelatedProducts } from "@/apis/queryFunctions/productFunctions";
import ProductCard from "@/components/Product/ProductCard";
import AppBreadcrumbs from "@/components/Common/AppBreadcrumbs";
import { Description } from "@/components/Common/Typography";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import { formatProductContent } from "@/utils/stripHTML";
import styles from "./productDetail.module.css";

interface ProductDetailClientProps {
  product: any;
  locale: string;
}

export default function ProductDetailClient({
  product,
  locale,
}: ProductDetailClientProps) {
  const { data: relatedProducts, isLoading: loadingRelated } =
    useFetchRelatedProducts({ ma_nvt: product?.ma_nvt });

  const isEn = locale === "en";

  const translated = useProductTranslation(
    {
      title: product?.ten_vt || "",
      description:
        product?.mieu_ta ||
        product?.chu_thich ||
        product?.content ||
        "",
    },
    locale
  );

  const breadcrumbs = [
    { title: isEn ? "Home" : "Trang chủ", href: `/${locale}` },
    { title: isEn ? "Products" : "Sản phẩm", href: `/${locale}/san-pham` },
    {
      title:
        translated.title ||
        product?.ten_vt ||
        (isEn ? "Product Details" : "Chi tiết sản phẩm"),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <Container>
        <div className={styles.breadcrumbWrap}>
          <AppBreadcrumbs items={breadcrumbs} />
        </div>

        <div className={styles.mainGrid}>
          {/* Left: Gallery */}
          <div className={styles.galleryCol}>
            <ProductGallery product={product} />
          </div>

          {/* Right: Info */}
          <div className={styles.infoCol}>
            <ProductInfo product={product} locale={locale} />

            {/* Related Products - Mini Sidebar Version */}
            <div className={styles.miniRelated}>
              <h3 className={styles.miniRelatedTitle}>
                {isEn ? "Related Products" : "Sản phẩm cùng dòng"}
              </h3>
              <div className={styles.miniRelatedGrid}>
                {(loadingRelated ? new Array(5).fill(null) : relatedProducts?.slice(0, 5) || []).map(
                  (item: any, i: number) => (
                    <ProductCard key={item?._id || i} product={item} isLoading={loadingRelated} isMini locale={locale} />
                  )
                )}
              </div>
            </div>
          </div>
        </div>

      {/* Bottom Accordion Sections */}
      <div className={styles.accordionContainer}>
        <AccordionItem title={isEn ? "Product Description" : "Mô tả sản phẩm"} defaultOpen>
          <Description
            as="div"
            size="medium"
            className={styles.descriptionContent}
            dangerouslySetInnerHTML={{
              __html: formatProductContent(
                translated.description ||
                product?.chu_thich ||
                product?.content ||
                product?.mieu_ta ||
                (isEn ? "No description available for this product." : "Chưa có mô tả cho sản phẩm này.")
              ),
            }}
          />
        </AccordionItem>

        {product?.add_on && product.add_on.length > 0 && (
          <>
            {product.add_on.map((item: any, idx: number) => (
              <AccordionItem key={idx} title={item.title}>
                <Description
                  as="div"
                  size="medium"
                  className={styles.descriptionContent}
                  dangerouslySetInnerHTML={{ __html: formatProductContent(item.content) }}
                />
              </AccordionItem>
            ))}
          </>
        )}
      </div>
    </Container>
  </div>
);
}

function AccordionItem({ title, children, defaultOpen = false }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  return (
    <div className={`${styles.accordionItem} ${isOpen ? styles.isOpen : ""}`}>
      <button className={styles.accordionHeader} onClick={() => setIsOpen(!isOpen)}>
        <span className={styles.accordionTitle}>{title}</span>
        <span className={styles.accordionIcon}>{isOpen ? "−" : "+"}</span>
      </button>
      <div className={styles.accordionContent}>
        <div className={styles.accordionInner}>
          {children}
        </div>
      </div>
    </div>
  );
}
